import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _ from "lodash";
import { useApp } from "../../design/AppContext";
import Empty from "../../components/design/Empty";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { MdChevronRight } from "react-icons/md";
import { StatusChip } from "../../components/mui";

/**
 * 自動化規則（DESIGN L626-L640，邏輯 L1035 / L1300）
 *
 * 每條規則是「當（觸發）→ 若（條件）→ 則（動作）」三段式，右側開關可即時啟停。
 */

/** 依 key 順序取第一個有值的欄位——後端可能回 snake_case 或 PascalCase */
const pick = (o, ...keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const v = _.get(o, keys[i]);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

/** 啟用旗標可能是 bool / 0-1 / "true"，一併吸收 */
const truthy = (v) => v === true || v === 1 || v === "1" || v === "true" || v === "True";

function App(props) {
  const { GET_AutomationList, POST_AutomationToggle, automationList } = props;
  const { toast } = useApp();

  // 樂觀切換：設計稿 toggleAuto 是先改畫面再提示，這裡沿用同樣手感，
  // 後端回應後再把該筆覆寫清掉，讓伺服器值成為唯一真相。
  const [override, setOverride] = useState({});

  const reload = useCallback(
    (done) => {
      GET_AutomationList(null, done || null, null);
    },
    [GET_AutomationList]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const rules = useMemo(
    () =>
      (Array.isArray(automationList) ? automationList : []).map((a, idx) => {
        const id = pick(a, "id", "Id", "rule_id", "ruleId");
        const name = String(pick(a, "name", "Name", "title", "Title") || "");
        return {
          key: id == null ? `auto-${idx}-${name}` : String(id),
          id,
          name,
          trigger: String(pick(a, "trigger_label", "triggerLabel", "trigger", "when") || "—"),
          cond: String(pick(a, "condition_label", "conditionLabel", "condition", "cond") || "—"),
          act: String(pick(a, "action_label", "actionLabel", "action", "act") || "—"),
          on: truthy(pick(a, "enabled", "Enabled", "is_enabled", "isEnabled", "on", "active")),
          runs: Number(pick(a, "runs", "run_count", "runCount", "runs_30d", "runs30d", "trigger_count") || 0),
        };
      }),
    [automationList]
  );

  const isOn = (r) => (override[r.key] === undefined ? r.on : override[r.key]);

  const handleToggle = (r) => {
    const next = !isOn(r);
    setOverride((o) => ({ ...o, [r.key]: next }));
    toast("已更新自動化規則");
    POST_AutomationToggle(
      { id: r.id, enabled: next },
      () => {
        // 先等重新載入的清單回到 store，再清掉樂觀覆寫；
        // 反過來做的話中間會有一瞬間讀到舊值，開關會閃回原本位置。
        reload(() =>
          setOverride((o) => {
            const n = { ...o };
            delete n[r.key];
            return n;
          })
        );
      },
      null
    );
  };

  return (
    <Card className="pg">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.25,
          px: 2.5,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h3" component="h1">
          自動化規則{" "}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            當（觸發）→ 若（條件）→ 則（動作）
          </Typography>
        </Typography>
        {/*
          這裡原本有一顆「新增規則」按鈕，已移除：它沒有接任何事件，是個空的入口。
          規則清單是後端給的，使用者能做的只有把個別規則開或關。

          舊註解在這裡列舉了引擎會做的事（通知 PM、自動指派把關者、標記逾期、鎖定績效），
          但那些都查證不到 —— C:/Project/iProject/api 裡沒有 AutomationController，
          grep Workflow 也是零命中。每一條規則實際會做什麼，以後端回來的說明為準，
          不要在前端替它宣告行為。
        */}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          系統內建規則，可個別啟用或停用
        </Typography>
      </Box>
      {rules.length === 0 ? (
        <Empty variant="plain" title="尚無自動化規則" pad="40px 20px" />
      ) : (
        <List disablePadding>
          {rules.map((a) => {
            const on = isOn(a);
            return (
              <ListItem key={a.key} divider sx={{ gap: 2, px: 2.5, py: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {a.name}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                    <StatusChip kind="progress" size="small">
                      當 {a.trigger}
                    </StatusChip>
                    <Box component="span" aria-hidden="true" sx={{ display: "flex", color: "text.disabled" }}>
                      <MdChevronRight />
                    </Box>
                    <StatusChip kind="closed" size="small">
                      若 {a.cond}
                    </StatusChip>
                    <Box component="span" aria-hidden="true" sx={{ display: "flex", color: "text.disabled" }}>
                      <MdChevronRight />
                    </Box>
                    <StatusChip kind="info" size="small">
                      則 {a.act}
                    </StatusChip>
                  </Stack>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body2" color="text.secondary">
                    近 30 天觸發
                  </Typography>
                  <Typography variant="h3" component="div">
                    {a.runs}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                  <Switch
                    checked={on}
                    onChange={() => handleToggle(a)}
                    slotProps={{ input: { "aria-label": a.name || "啟用此規則" } }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: on ? "primary.main" : "text.secondary" }}
                  >
                    {on ? "啟用中" : "已停用"}
                  </Typography>
                </Box>
              </ListItem>
            );
          })}
        </List>
      )}
    </Card>
  );
}

const mapStateToProps = (state) => ({
  automationList: _.get(state, "Automation.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_AutomationList: (payload, callback, loading) =>
    dispatch({ type: "GET_AutomationList", payload, callback, loading }),
  POST_AutomationToggle: (payload, callback, loading) =>
    dispatch({ type: "POST_AutomationToggle", payload, callback, loading }),
});

App.propTypes = {
  automationList: PropTypes.array,
  GET_AutomationList: PropTypes.func,
  POST_AutomationToggle: PropTypes.func,
};

App.defaultProps = {
  automationList: [],
  GET_AutomationList: () => {},
  POST_AutomationToggle: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
